import { createCsvBlob } from '../csvEncoding'

import {
  csvEscape,
  downloadBlob,
  ensureStatisticsPdfFonts,
  fileStamp,
  format2
} from './common'

const format3 = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? number.toFixed(3) : '0.000'
}

const getHeaders = (t, statsMode) => {
  if (statsMode === 'electric') {
    return [
      t('TRIPLOG.STATISTICS.VEHICLES.VEHICLE_NAME', {
        defaultValue: 'Názov vozidla'
      }),
      t('TRIPLOG.STATISTICS.VEHICLES.LICENSE_PLATE', {
        defaultValue: 'Evidenčné číslo'
      }),
      t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_TRIPS', {
        defaultValue: 'Počet jázd'
      }),
      t('TRIPLOG.STATISTICS.VEHICLES.KM', {
        defaultValue: 'Najazdené km'
      }),
      t('TRIPLOG.STATISTICS.EV.TOTAL_KWH', {
        defaultValue: 'Nabité'
      }),
      t('TRIPLOG.STATISTICS.EV.KWH_PER_100KM', {
        defaultValue: 'Energia / 100 km'
      }),
      t('TRIPLOG.STATISTICS.EV.TOTAL_COST', {
        defaultValue: 'Náklady na nabíjanie'
      }),
      t('TRIPLOG.STATISTICS.EV.COST_PER_100KM', {
        defaultValue: 'Náklady / 100 km'
      }),
      t('TRIPLOG.STATISTICS.EV.AVG_PRICE', {
        defaultValue: 'Priemerná cena'
      })
    ]
  }

  return [
    t('TRIPLOG.STATISTICS.VEHICLES.VEHICLE_NAME', {
      defaultValue: 'Názov vozidla'
    }),
    t('TRIPLOG.STATISTICS.VEHICLES.LICENSE_PLATE', {
      defaultValue: 'Evidenčné číslo'
    }),
    t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_TRIPS', {
      defaultValue: 'Počet jázd'
    }),
    t('TRIPLOG.STATISTICS.VEHICLES.KM', {
      defaultValue: 'Najazdené km'
    }),
    t('TRIPLOG.STATISTICS.VEHICLES.COST', {
      defaultValue: 'Náklady'
    }),
    t('TRIPLOG.STATISTICS.VEHICLES.CONSUMPTION', {
      defaultValue: 'Spotreba'
    }),
    t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_FUEL', {
      defaultValue: 'Množstvo paliva'
    })
  ]
}

export const buildVehicleExportBlock = (
  vehicles,
  periodText,
  statsMode
) => ({
  vehicles: (vehicles || []).map((vehicle) => ({
    name:
      vehicle.name ||
      vehicle.model ||
      '',

    plate:
      vehicle.license ||
      vehicle.licensePlate ||
      vehicle.spz ||
      '',

    trips:
      Number(vehicle.trips || 0),

    km:
      format2(vehicle.km),

    cost:
      format2(
        vehicle.totalCost ??
        vehicle.cost
      ),

    consumption:
      format2(
        vehicle.avgConsumption ??
        vehicle.consumption
      ),

    liters:
      format2(
        vehicle.fuel ??
        vehicle.liters ??
        0
      ),

    electricKwh:
      format2(vehicle.electricKwh),

    electricKwhPer100Km:
      format2(vehicle.electricKwhPer100Km),

    electricCost:
      format2(vehicle.electricCost),

    electricCostPer100Km:
      format2(vehicle.electricCostPer100Km),

    avgElectricPrice:
      format3(vehicle.avgElectricPrice)
  })),

  meta: {
    period: periodText,
    statsMode
  }
})

const getVehicleRows = (block) => {
  if (block?.meta?.statsMode === 'electric') {
    return (block?.vehicles || []).map((vehicle) => [
      vehicle.name,
      vehicle.plate,
      vehicle.trips,
      vehicle.km,
      vehicle.electricKwh,
      vehicle.electricKwhPer100Km,
      vehicle.electricCost,
      vehicle.electricCostPer100Km,
      vehicle.avgElectricPrice
    ])
  }

  return (block?.vehicles || []).map((vehicle) => [
    vehicle.name,
    vehicle.plate,
    vehicle.trips,
    vehicle.km,
    vehicle.cost,
    vehicle.consumption,
    vehicle.liters
  ])
}

export const exportVehiclesCSV = (block, t) => {
  const headers = getHeaders(t, block?.meta?.statsMode)
  const rows = getVehicleRows(block)

  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.VEHICLES_TAB', {
        defaultValue: 'Vozidlá'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    []
  ]

  const csv = [
    ...titleRows,
    headers,
    ...rows
  ]
    .map((row) =>
      row.map(csvEscape).join(';')
    )
    .join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `vehicles_statistics_${fileStamp()}.csv`
  )
}

export const exportVehiclesXLS = async (
  block,
  t
) => {
  const XLSX = await import('xlsx-js-style')

  const headers = getHeaders(t, block?.meta?.statsMode)
  const rows = getVehicleRows(block)

  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.VEHICLES_TAB', {
        defaultValue: 'Vozidlá'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    []
  ]

  const worksheetData = [
    ...titleRows,
    headers,
    ...rows
  ]

  const ws = XLSX.utils.aoa_to_sheet(
    worksheetData
  )

  ws['!cols'] = headers.map(
    (_, columnIndex) => ({
      wch:
        columnIndex === 0
          ? 24
          : columnIndex === 1
            ? 20
            : 18
    })
  )

  const mainTitleCell =
    XLSX.utils.encode_cell({
      r: 0,
      c: 0
    })

  if (ws[mainTitleCell]) {
    ws[mainTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 14,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  const subTitleCell =
    XLSX.utils.encode_cell({
      r: 1,
      c: 0
    })

  if (ws[subTitleCell]) {
    ws[subTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 11,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  const periodLabelCell =
    XLSX.utils.encode_cell({
      r: 3,
      c: 0
    })

  if (ws[periodLabelCell]) {
    ws[periodLabelCell].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  const periodValueCell =
    XLSX.utils.encode_cell({
      r: 3,
      c: 1
    })

  if (ws[periodValueCell]) {
    ws[periodValueCell].s = {
      font: {
        name: 'Arial',
        sz: 10
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  const headerRowIndex =
    titleRows.length

  headers.forEach(
    (_, columnIndex) => {
      const cellAddress =
        XLSX.utils.encode_cell({
          r: headerRowIndex,
          c: columnIndex
        })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        fill: {
          fgColor: {
            rgb: 'F0F0F0'
          }
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    }
  )

  rows.forEach((row, rowIndex) => {
    row.forEach(
      (_, columnIndex) => {
        const cellAddress =
          XLSX.utils.encode_cell({
            r:
              headerRowIndex +
              1 +
              rowIndex,
            c: columnIndex
          })

        if (!ws[cellAddress]) return

        ws[cellAddress].s = {
          font: {
            name: 'Arial',
            sz: 10
          },
          alignment: {
            horizontal: 'left',
            vertical: 'center'
          }
        }
      }
    )
  })

  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    t('TRIPLOG.STATISTICS.VEHICLES_TAB', {
      defaultValue: 'Vozidlá'
    })
  )

  XLSX.writeFile(
    wb,
    `vehicles_statistics_${fileStamp()}.xlsx`
  )
}

export const exportVehiclesPDF = async (
  block,
  t
) => {
  const { jsPDF } =
    await import('jspdf')

  const autoTableModule =
    await import('jspdf-autotable')

  const autoTable =
    autoTableModule.default ||
    autoTableModule.autoTable

  const headers = getHeaders(t, block?.meta?.statsMode)
  const dataRows = getVehicleRows(block)

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureStatisticsPdfFonts(doc)

  const marginLeft = 18
  const marginRight = 18

  const pageWidth =
    doc.internal.pageSize.getWidth()

  const usableWidth =
    pageWidth -
    marginLeft -
    marginRight

  const columnWidth =
    usableWidth /
    Math.max(headers.length, 1)

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)

  doc.text(
    t('TRIPLOG.STATISTICS.TITLE', {
      defaultValue: 'Štatistiky'
    }),
    marginLeft,
    28
  )

  doc.setFontSize(11)

  doc.text(
    t('TRIPLOG.STATISTICS.VEHICLES_TAB', {
      defaultValue: 'Vozidlá'
    }),
    marginLeft,
    44
  )

  doc.setFontSize(9)

  doc.text(
    `${t(
      'TRIPLOG.STATISTICS.SHOWN_PERIOD',
      {
        defaultValue:
          'Zobrazené obdobie'
      }
    )}: ${block?.meta?.period || ''}`,
    marginLeft,
    60
  )

  autoTable(doc, {
    head: [headers],
    body: dataRows,
    startY: 74,
    theme: 'grid',

    margin: {
      top: 18,
      right: marginRight,
      bottom: 18,
      left: marginLeft
    },

    tableWidth: usableWidth,

    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: block?.meta?.statsMode === 'electric' ? 7 : 8,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 12
    },

    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: block?.meta?.statsMode === 'electric' ? 7 : 8,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },

    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },

    columnStyles: headers.reduce(
      (result, _header, index) => {
        result[index] = {
          cellWidth: columnWidth
        }

        return result
      },
      {}
    ),

    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(
    doc.output('blob'),
    `vehicles_statistics_${fileStamp()}.pdf`
  )
}